const catalogueReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

const catalogueSelectors = {
  root: '[data-dark-mile-catalogue]',
  results: '[data-dark-mile-catalogue-results]',
  status: '[data-dark-mile-catalogue-status]',
  tabs: '[data-dark-mile-category-tabs]',
  categoryLink: '[data-dark-mile-category-link]',
  productLink: '[data-dark-mile-product-link]',
};

function isModifiedClick(event) {
  return event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
}

function waitForCatalogueExit() {
  if (catalogueReducedMotion.matches) return Promise.resolve();
  return new Promise((resolve) => window.setTimeout(resolve, 170));
}

class DarkMileCatalogue {
  constructor(root) {
    this.root = root;
    this.results = root.querySelector(catalogueSelectors.results);
    this.status = root.querySelector(catalogueSelectors.status);
    this.request = null;
    this.onChange = this.onChange.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onPopState = this.onPopState.bind(this);
  }

  connect() {
    if (!this.results || this.root.dataset.catalogueReady === 'true') return;
    this.root.addEventListener('change', this.onChange);
    this.root.dataset.catalogueReady = 'true';
    this.root.addEventListener('click', this.onClick);
    window.addEventListener('popstate', this.onPopState);
    history.replaceState({ ...history.state, darkMileCatalogue: true }, '', window.location.href);
  }

  onClick(event) {
    const categoryLink = event.target.closest(catalogueSelectors.categoryLink);
    if (categoryLink && this.root.contains(categoryLink)) {
      if (isModifiedClick(event) || categoryLink.target === '_blank' || categoryLink.origin !== window.location.origin) return;
      event.preventDefault();
      if (categoryLink.href === window.location.href) return;
      this.load(categoryLink.href, { push: true, restoreFocus: event.detail === 0 });
      return;
    }

    const productLink = event.target.closest(catalogueSelectors.productLink);
    if (!productLink || !this.root.contains(productLink) || isModifiedClick(event) || catalogueReducedMotion.matches) return;
    const image = productLink.closest('.dark-mile-studio-card')?.querySelector('[data-dark-mile-transition-image]');
    if (image) image.style.viewTransitionName = 'dm-product-image';
    if (!('startViewTransition' in document)) {
      event.preventDefault();
      this.root.classList.add(productLink.dataset.fallbackClass || 'dark-mile-page-leaving');
      window.setTimeout(() => window.location.assign(productLink.href), 180);
    }
  }

  onPopState() {
    this.load(window.location.href, { push: false, restoreFocus: false });
  }

  onChange(event) {
    if (!event.target.matches('[data-dark-mile-catalogue-sort]')) return;
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', event.target.value);
    url.searchParams.delete('page');
    this.load(url.href, { push: true, restoreFocus: false });
  }

  async load(url, { push, restoreFocus }) {
    this.request?.abort();
    this.request = new AbortController();
    const request = this.request;
    this.results.setAttribute('aria-busy', 'true');
    this.root.classList.add('is-filtering');
    this.results.classList.add('is-filtering-out');

    try {
      const responsePromise = fetch(url, {
        signal: request.signal,
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'DarkMileCatalogue' },
      });
      await waitForCatalogueExit();
      const response = await responsePromise;
      if (!response.ok) throw new Error(`Catalogue request failed: ${response.status}`);
      const html = await response.text();
      const nextDocument = new DOMParser().parseFromString(html, 'text/html');
      const nextRoot = nextDocument.querySelector(catalogueSelectors.root);
      const nextResults = nextRoot?.querySelector(catalogueSelectors.results);
      const nextTabs = nextRoot?.querySelector(catalogueSelectors.tabs);
      if (!nextResults || !nextTabs) throw new Error('Catalogue response is incomplete');
      if (request !== this.request) return;

      this.results.innerHTML = nextResults.innerHTML;
      this.results.dataset.sort = nextResults.dataset.sort || 'manual';
      const sort = this.root.querySelector('[data-dark-mile-catalogue-sort]');
      if (sort) {
        sort.value = this.results.dataset.sort;
        sort.form.action = new URL(response.url || url, window.location.href).pathname;
      }
      const tabs = this.root.querySelector(catalogueSelectors.tabs);
      if (tabs) tabs.innerHTML = nextTabs.innerHTML;
      this.updateTopNavigation(new URL(response.url || url, window.location.href));
      if (nextDocument.title) document.title = nextDocument.title;
      if (push) history.pushState({ darkMileCatalogue: true }, '', response.url || url);

      this.results.classList.remove('is-filtering-out');
      this.results.classList.add('is-filtering-in');
      void this.results.offsetWidth;
      requestAnimationFrame(() => this.results.classList.remove('is-filtering-in'));
      const count = this.results.querySelectorAll('.dark-mile-studio-card').length;
      if (this.status) this.status.textContent = `${count} product${count === 1 ? '' : 's'} shown`;
      if (restoreFocus) {
        const active = this.root.querySelector(`${catalogueSelectors.tabs} [aria-current="page"]`);
        active?.focus({ preventScroll: true });
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      window.location.assign(url);
    } finally {
      if (request === this.request) {
        this.results.setAttribute('aria-busy', 'false');
        this.results.classList.remove('is-filtering-out');
        this.root.classList.remove('is-filtering');
      }
    }
  }

  updateTopNavigation(destination) {
    this.root.querySelectorAll(`.dark-mile-studio-index__header ${catalogueSelectors.categoryLink}`).forEach((link) => {
      if (link.closest(catalogueSelectors.tabs)) return;
      const linkUrl = new URL(link.href, window.location.href);
      const active = linkUrl.pathname === destination.pathname;
      link.classList.toggle('is-active', active);
      if (active) link.setAttribute('aria-current', 'page');
      else link.removeAttribute('aria-current');
    });
  }
}

function setupDarkMileCatalogueTransitions(scope = document) {
  scope.querySelectorAll(catalogueSelectors.root).forEach((root) => new DarkMileCatalogue(root).connect());
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => setupDarkMileCatalogueTransitions(), { once: true });
} else {
  setupDarkMileCatalogueTransitions();
}

document.addEventListener('shopify:section:load', (event) => setupDarkMileCatalogueTransitions(event.target));
